import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, useTheme } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Users, Radio, Video, Calendar,
  ChevronRight, ArrowLeft, Loader2, CheckCircle, X,
  Clock, Layers, Timer, Save, Link as LinkIcon, AlertCircle,
  FileText, Upload, Download, ExternalLink, Play, Eye,
  Sparkles, Check, RefreshCw, Film, ArrowUpRight, CheckCheck, Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getVideoEmbedInfo } from "@/lib/videoEmbed";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric"
    });
  } catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true
    });
  } catch { return iso; }
};

const pad = (v) => String(v).padStart(2, "0");
const toLocalDateValue = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const timeOptions = Array.from({ length: 56 }, (_, index) => {
  const totalMinutes = 6 * 60 + index * 15;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const value = `${pad(hours)}:${pad(minutes)}`;
  const label = new Date(2026, 0, 1, hours, minutes).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit"
  });
  return { value, label };
});

const durationOptions = [30, 45, 60, 75, 90, 120, 150, 180];

// ── Form Helpers with High Contrast in Both Modes ──
const FormInput = ({ label, name, value, onChange, type = "text", placeholder, isLight, required }) => (
  <div className="flex flex-col gap-1.5">
    <label className={`text-xs font-bold uppercase tracking-wider ${isLight ? "text-slate-800" : "text-zinc-200"}`}>
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium outline-none transition-all ${
        isLight
          ? "bg-slate-50 border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
          : "bg-black/40 border-white/15 text-white placeholder-zinc-500 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
      }`}
    />
  </div>
);

const FormTextarea = ({ label, name, value, onChange, placeholder, isLight, rows = 3 }) => (
  <div className="flex flex-col gap-1.5">
    <label className={`text-xs font-bold uppercase tracking-wider ${isLight ? "text-slate-800" : "text-zinc-200"}`}>
      {label}
    </label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium outline-none transition-all resize-none ${
        isLight
          ? "bg-slate-50 border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
          : "bg-black/40 border-white/15 text-white placeholder-zinc-500 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
      }`}
    />
  </div>
);

// ── Video Player Modal ─────────────────────────
const VideoPlayerModal = ({ session, onClose, isLight }) => {
  const [copiedPass, setCopiedPass] = useState(false);
  if (!session || !session.recording_url) return null;
  const embed = getVideoEmbedInfo(session.recording_url);

  const handleCopyPass = () => {
    if (!session.recording_password) return;
    navigator.clipboard.writeText(session.recording_password);
    setCopiedPass(true);
    toast.success("Passcode copied to clipboard!");
    setTimeout(() => setCopiedPass(false), 2000);
  };

  const isZoom = embed.isZoom || (session.recording_url && session.recording_url.includes("zoom.us")) || session.recording_source === "zoom_cloud";

  return (
    <Dialog open={!!session} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`max-w-3xl w-full p-5 rounded-3xl border shadow-2xl ${
        isLight ? "bg-white border-slate-200" : "bg-[#0E121B] border-white/15"
      }`}>
        <DialogHeader className="mb-3">
          <DialogTitle className={`font-outfit text-lg font-black flex items-center justify-between ${
            isLight ? "text-slate-900" : "text-white"
          }`}>
            <span className="truncate pr-4">{session.title}</span>
          </DialogTitle>
          {session.description && (
            <DialogDescription className="text-xs text-slate-500 dark:text-zinc-400">
              {session.description}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="relative aspect-video w-full rounded-2xl overflow-hidden bg-black shadow-2xl border border-black/40">
          {isZoom ? (
            <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center bg-gradient-to-br from-[#0c1a2e] via-[#0E121B] to-[#121028]">
              <div className="w-16 h-16 rounded-2xl bg-[#2D8CFF]/20 border border-[#2D8CFF]/40 flex items-center justify-center text-[#2D8CFF] mb-3 shadow-lg shadow-[#2D8CFF]/20">
                <Video className="w-8 h-8" />
              </div>
              <span className="text-[11px] font-black tracking-wider uppercase px-3 py-1 rounded-full bg-[#2D8CFF]/20 text-[#2D8CFF] border border-[#2D8CFF]/40 mb-2">
                Zoom Cloud Recording
              </span>
              <h3 className="text-base sm:text-lg font-black text-white mb-1.5 max-w-md truncate">
                {session.title}
              </h3>
              <p className="text-xs text-zinc-400 max-w-sm mb-4">
                Official cloud recording hosted securely on Zoom. Includes full high-definition video, audio, and speaker view.
              </p>

              {session.recording_password && (
                <div className="mb-4 px-3.5 py-1.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2 text-xs text-zinc-300">
                  <span className="text-zinc-500 font-bold">Passcode:</span>
                  <code className="font-mono font-bold text-amber-400">{session.recording_password}</code>
                  <button
                    onClick={handleCopyPass}
                    className="p-1 hover:text-white text-zinc-400 transition-colors"
                    title="Copy passcode"
                  >
                    {copiedPass ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              )}

              <a
                href={session.recording_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl bg-gradient-to-r from-[#2D8CFF] to-[#166ee0] hover:from-[#3a94ff] hover:to-[#2279ec] text-white font-black text-sm shadow-xl shadow-[#2D8CFF]/30 transition-all transform hover:scale-[1.02]"
              >
                <Play className="w-4 h-4 fill-white" />
                Play Recording on Zoom
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>
          ) : embed.embedUrl ? (
            <iframe
              src={embed.embedUrl}
              title={session.title}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video
              src={session.recording_url}
              controls
              autoPlay
              className="w-full h-full object-contain"
            />
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 mt-3 pt-3 border-t border-slate-100 dark:border-white/10 font-medium">
          <span>{fmtDateTime(session.scheduled_at)} · {session.duration || 90} mins</span>
          <a
            href={session.recording_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-600 dark:text-violet-400 hover:underline flex items-center gap-1 font-bold"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open Link in New Tab
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Main Admin Batches Content ───────────────────
export function AdminBatchesContent({ embedded = false }) {
  const { theme } = useTheme();
  const isLight = theme === "light";

  // Navigation / Selection
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(true);

  // Batch Form State
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [editBatch, setEditBatch] = useState(null);
  const [savingBatch, setSavingBatch] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(null);
  const [batchForm, setBatchForm] = useState({
    name: "", description: "", start_date: "", end_date: "", max_students: "", is_active: true, is_running_batch: false
  });

  // Batch Detail Room State
  const [activeTab, setActiveTab] = useState("live"); // "live" | "recordings" | "materials"
  const [sessions, setSessions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  // Quick Inline Link Paster State (for automating recorded class links!)
  const [quickLinks, setQuickLinks] = useState({});
  const [savingQuickLink, setSavingQuickLink] = useState(null);

  // Live Class Scheduling Modal
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [savingSession, setSavingSession] = useState(false);
  const [uploadingLivePdf, setUploadingLivePdf] = useState(false);
  const [zoomStatus, setZoomStatus] = useState({ configured: false });
  const [liveForm, setLiveForm] = useState({
    title: "",
    description: "",
    scheduled_date: toLocalDateValue(new Date()),
    scheduled_time: "19:00",
    duration: 90,
    source: "manual", // "zoom" | "manual"
    meeting_link: "",
    pdf_url: "",
    pdf_name: ""
  });

  // Standalone Recording Modal
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [savingRecording, setSavingRecording] = useState(false);
  const [uploadingSessionPdf, setUploadingSessionPdf] = useState(false);
  const [recordingForm, setRecordingForm] = useState({
    title: "",
    description: "",
    recording_url: "",
    recording_password: "",
    duration: 90,
    scheduled_date: toLocalDateValue(new Date()),
    pdf_url: "",
    pdf_name: ""
  });

  // Material (PDF) Modal
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [savingMaterial, setSavingMaterial] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [materialForm, setMaterialForm] = useState({
    title: "",
    description: "",
    file_url: "",
    file_name: "",
    file_size: ""
  });
  const fileInputRef = useRef(null);

  // Video Watcher Modal
  const [playingSession, setPlayingSession] = useState(null);

  // Fetch all batches
  const fetchBatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/batches`, { withCredentials: true });
      setBatches(res.data || []);
      if (selectedBatch) {
        const updated = (res.data || []).find(b => b.batch_id === selectedBatch.batch_id);
        if (updated) setSelectedBatch(updated);
      }
    } catch {
      toast.error("Failed to load batches");
    } finally {
      setLoading(false);
    }
  }, [selectedBatch]);

  useEffect(() => {
    fetchBatches();
  }, []);

  // Fetch Zoom Status for live scheduling
  useEffect(() => {
    axios.get(`${API}/live-classes/zoom-status`, { withCredentials: true })
      .then(res => {
        setZoomStatus(res.data || { configured: false });
        if (res.data?.configured) {
          setLiveForm(prev => ({ ...prev, source: "zoom" }));
        }
      })
      .catch(() => {});
  }, []);

  // Fetch sessions for the selected batch
  const fetchSessions = useCallback(async () => {
    if (!selectedBatch) return;
    setLoadingSessions(true);
    try {
      const res = await axios.get(`${API}/batches/${selectedBatch.batch_id}/sessions`, { withCredentials: true });
      setSessions(res.data || []);
    } catch {
      toast.error("Failed to load batch sessions");
    } finally {
      setLoadingSessions(false);
    }
  }, [selectedBatch]);

  // Fetch materials for the selected batch
  const fetchMaterials = useCallback(async () => {
    if (!selectedBatch) return;
    setLoadingMaterials(true);
    try {
      const res = await axios.get(`${API}/batches/${selectedBatch.batch_id}/materials`, { withCredentials: true });
      setMaterials(res.data || []);
    } catch {
      toast.error("Failed to load batch study materials");
    } finally {
      setLoadingMaterials(false);
    }
  }, [selectedBatch]);

  useEffect(() => {
    if (selectedBatch) {
      fetchSessions();
      fetchMaterials();
    }
  }, [selectedBatch, fetchSessions, fetchMaterials]);

  // ── Quick Paste Recording Link (Automating workflow!) ──
  const handleQuickSaveRecording = async (session) => {
    const link = (quickLinks[session.session_id] || "").trim();
    if (!link) {
      toast.error("Please paste a valid video URL first");
      return;
    }
    setSavingQuickLink(session.session_id);
    try {
      await axios.put(
        `${API}/batches/${selectedBatch.batch_id}/sessions/${session.session_id}`,
        { recording_url: link },
        { withCredentials: true }
      );
      toast.success(`Recording link attached to "${session.title}"! Students can now watch.`);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save recording link");
    } finally {
      setSavingQuickLink(null);
    }
  };

  // ── Auto-Sync Zoom Cloud Recording (Completely automatic zero-paste workflow!) ──
  const [syncingZoomSession, setSyncingZoomSession] = useState(null);
  const [syncingAllZoom, setSyncingAllZoom] = useState(false);
  const [manualPasteOpen, setManualPasteOpen] = useState({});

  const handleSyncZoomSession = async (session) => {
    if (!session || !selectedBatch) return;
    setSyncingZoomSession(session.session_id);
    try {
      const res = await axios.post(
        `${API}/batches/${selectedBatch.batch_id}/sessions/${session.session_id}/sync-zoom-recording`,
        {},
        { withCredentials: true }
      );
      if (res.data?.synced && res.data?.session?.recording_url) {
        toast.success(`🎉 Zoom Cloud Recording attached to "${session.title}"! Students can now watch.`);
        fetchSessions();
      } else {
        toast.info(res.data?.message || "Zoom recording is still processing on Zoom Cloud. Please check back in a couple minutes.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not check Zoom recording status");
    } finally {
      setSyncingZoomSession(null);
    }
  };

  const handleSyncAllZoomRecordings = async () => {
    if (!selectedBatch) return;
    setSyncingAllZoom(true);
    try {
      const res = await axios.post(
        `${API}/batches/${selectedBatch.batch_id}/sync-zoom-recordings`,
        {},
        { withCredentials: true }
      );
      if (res.data?.synced_count > 0) {
        toast.success(`🎉 Automatically synced ${res.data.synced_count} Zoom cloud recording(s)!`);
        fetchSessions();
      } else {
        toast.info(res.data?.total_scanned > 0 ? "Zoom is still encoding pending recordings. They will attach automatically once ready." : "All Zoom recordings in this batch are already up to date.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to scan Zoom recordings");
    } finally {
      setSyncingAllZoom(false);
    }
  };

  // Automated background check on tab open if any zoom session lacks recording
  useEffect(() => {
    if (activeTab === "recordings" && selectedBatch && sessions.length > 0) {
      const pendingZoom = sessions.some(s => (s.zoom_meeting_id || s.source === "zoom") && !s.recording_url);
      if (pendingZoom) {
        axios.post(
          `${API}/batches/${selectedBatch.batch_id}/sync-zoom-recordings`,
          {},
          { withCredentials: true }
        ).then(res => {
          if (res.data?.synced_count > 0) {
            toast.success(`🎉 Automatically fetched ${res.data.synced_count} Zoom cloud recording(s)!`);
            fetchSessions();
          }
        }).catch(() => {});
      }
    }
  }, [activeTab, selectedBatch, sessions.length]);

  // ── Batch CRUD Handlers ───────────────────────
  const openCreateBatch = () => {
    setEditBatch(null);
    setBatchForm({ name: "", description: "", start_date: "", end_date: "", max_students: "", is_active: true, is_running_batch: false });
    setShowBatchForm(true);
  };

  const openEditBatch = (b, e) => {
    if (e) e.stopPropagation();
    setEditBatch(b);
    setBatchForm({
      name: b.name || "",
      description: b.description || "",
      start_date: b.start_date ? b.start_date.slice(0, 10) : "",
      end_date: b.end_date ? b.end_date.slice(0, 10) : "",
      max_students: b.max_students || "",
      is_active: b.is_active !== false,
      is_running_batch: Boolean(b.is_running_batch),
    });
    setShowBatchForm(true);
  };

  const handleSaveBatch = async () => {
    if (!batchForm.name.trim()) {
      toast.error("Batch name is required");
      return;
    }
    setSavingBatch(true);
    try {
      const payload = {
        ...batchForm,
        max_students: batchForm.max_students ? parseInt(batchForm.max_students) : null,
        is_running_batch: Boolean(batchForm.is_running_batch)
      };
      if (editBatch) {
        await axios.put(`${API}/batches/${editBatch.batch_id}`, payload, { withCredentials: true });
        toast.success("Batch updated successfully");
      } else {
        await axios.post(`${API}/batches`, payload, { withCredentials: true });
        toast.success("Batch created successfully!");
      }
      setShowBatchForm(false);
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save batch");
    } finally {
      setSavingBatch(false);
    }
  };

  const handleDeleteBatch = async (b, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Delete batch "${b.name}"? All sessions and materials will be removed.`)) return;
    setDeletingBatch(b.batch_id);
    try {
      await axios.delete(`${API}/batches/${b.batch_id}`, { withCredentials: true });
      toast.success("Batch deleted");
      if (selectedBatch?.batch_id === b.batch_id) setSelectedBatch(null);
      fetchBatches();
    } catch {
      toast.error("Failed to delete batch");
    } finally {
      setDeletingBatch(null);
    }
  };

  const handleToggleDashboard = async (b, shouldShow, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.put(`${API}/batches/${b.batch_id}`, { is_running_batch: shouldShow }, { withCredentials: true });
      toast.success(shouldShow ? `Batch "${b.name}" is now featured on Student Dashboard!` : `Batch "${b.name}" removed from Student Dashboard (only in Batches section now)`);
      fetchBatches();
    } catch {
      toast.error("Failed to update dashboard visibility");
    }
  };

  // ── Live Class Handlers ────────────────────────
  const openScheduleLive = () => {
    setEditSession(null);
    setLiveForm({
      title: "",
      description: "",
      scheduled_date: toLocalDateValue(new Date()),
      scheduled_time: "19:00",
      duration: 90,
      source: zoomStatus.configured ? "zoom" : "manual",
      meeting_link: "",
      pdf_url: "",
      pdf_name: ""
    });
    setShowLiveModal(true);
  };

  const openEditLive = (s) => {
    setEditSession(s);
    const d = s.scheduled_at ? new Date(s.scheduled_at) : new Date();
    setLiveForm({
      title: s.title || "",
      description: s.description || "",
      scheduled_date: toLocalDateValue(d),
      scheduled_time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      duration: s.duration || 90,
      source: s.source || "manual",
      meeting_link: s.meeting_link || "",
      pdf_url: s.pdf_url || "",
      pdf_name: s.pdf_name || ""
    });
    setShowLiveModal(true);
  };

  const handleSaveLiveClass = async () => {
    if (!liveForm.title.trim()) {
      toast.error("Class title is required");
      return;
    }
    if (liveForm.source === "manual" && !liveForm.meeting_link.trim()) {
      toast.error("Please enter a meeting link (Zoom, Google Meet, etc.)");
      return;
    }
    setSavingSession(true);
    try {
      const scheduled_at = `${liveForm.scheduled_date}T${liveForm.scheduled_time}:00`;
      const payload = {
        batch_id: selectedBatch.batch_id,
        title: liveForm.title,
        description: liveForm.description,
        scheduled_at,
        duration: parseInt(liveForm.duration) || 90,
        source: liveForm.source,
        meeting_link: liveForm.meeting_link,
        session_type: "live",
        pdf_url: (liveForm.pdf_url || "").trim(),
        pdf_name: (liveForm.pdf_name || "").trim()
      };

      if (editSession) {
        await axios.put(`${API}/batches/${selectedBatch.batch_id}/sessions/${editSession.session_id}`, payload, { withCredentials: true });
        toast.success("Live class updated");
      } else {
        await axios.post(`${API}/batches/${selectedBatch.batch_id}/sessions`, payload, { withCredentials: true });
        toast.success(`Live class scheduled! It is also ready on the Recorded Classes page.`);
      }
      setShowLiveModal(false);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to schedule live class");
    } finally {
      setSavingSession(false);
    }
  };

  // ── Standalone / Old Recording Handlers ──────────────────
  const openAddRecording = (fromSession = null) => {
    if (fromSession) {
      setRecordingForm({
        title: fromSession.title,
        description: fromSession.description || "",
        recording_url: fromSession.recording_url || "",
        recording_password: fromSession.recording_password || "",
        duration: fromSession.duration || 90,
        scheduled_date: fromSession.scheduled_at ? toLocalDateValue(new Date(fromSession.scheduled_at)) : toLocalDateValue(new Date()),
        pdf_url: fromSession.pdf_url || "",
        pdf_name: fromSession.pdf_name || ""
      });
      setEditSession(fromSession);
    } else {
      setEditSession(null);
      setRecordingForm({
        title: "",
        description: "",
        recording_url: "",
        recording_password: "",
        duration: 90,
        scheduled_date: toLocalDateValue(new Date()),
        pdf_url: "",
        pdf_name: ""
      });
    }
    setShowRecordingModal(true);
  };

  const handleSaveRecording = async () => {
    if (!recordingForm.title.trim() || !recordingForm.recording_url.trim()) {
      toast.error("Title and recording URL are required");
      return;
    }
    setSavingRecording(true);
    try {
      const scheduled_at = `${recordingForm.scheduled_date}T10:00:00`;
      const payload = {
        title: recordingForm.title.trim(),
        description: recordingForm.description.trim(),
        recording_url: recordingForm.recording_url.trim(),
        recording_password: (recordingForm.recording_password || "").trim(),
        duration: parseInt(recordingForm.duration) || 90,
        pdf_url: (recordingForm.pdf_url || "").trim(),
        pdf_name: (recordingForm.pdf_name || "").trim(),
      };
      if (editSession) {
        await axios.put(
          `${API}/batches/${selectedBatch.batch_id}/sessions/${editSession.session_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success("Recording updated");
      } else {
        await axios.post(
          `${API}/batches/${selectedBatch.batch_id}/sessions`,
          {
            ...payload,
            batch_id: selectedBatch.batch_id,
            scheduled_at,
            session_type: "recording",
            source: recordingForm.recording_url.includes("zoom.us") ? "zoom" : "manual",
            recording_source: recordingForm.recording_url.includes("zoom.us") ? "zoom_cloud" : "manual"
          },
          { withCredentials: true }
        );
        toast.success("Past recording uploaded to batch successfully!");
      }
      setShowRecordingModal(false);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save recording");
    } finally {
      setSavingRecording(false);
    }
  };

  const handleDeleteSession = async (session_id) => {
    if (!window.confirm("Are you sure you want to delete this session?")) return;
    try {
      await axios.delete(`${API}/batches/${selectedBatch.batch_id}/sessions/${session_id}`, { withCredentials: true });
      toast.success("Session deleted");
      fetchSessions();
    } catch {
      toast.error("Failed to delete session");
    }
  };

  // ── PDF Material Handlers ───────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error("File size exceeds 50MB limit");
      return;
    }

    setUploadingPdf(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("kind", "pdf");

    try {
      const res = await axios.post(`${API}/uploads`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      const fileUrl = res.data?.file_url || `${API}/uploads/${res.data?.file_id}`;
      const fileSize = `${(file.size / (1024 * 1024)).toFixed(2)} MB`;

      setMaterialForm(prev => ({
        ...prev,
        file_url: fileUrl,
        file_name: file.name,
        file_size: fileSize,
        title: prev.title || file.name.replace(/\.[^/.]+$/, "")
      }));
      toast.success("PDF uploaded successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "File upload failed");
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSaveMaterial = async () => {
    if (!materialForm.title.trim() || !materialForm.file_url.trim()) {
      toast.error("Document title and PDF file are required");
      return;
    }
    setSavingMaterial(true);
    try {
      await axios.post(
        `${API}/batches/${selectedBatch.batch_id}/materials`,
        materialForm,
        { withCredentials: true }
      );
      toast.success("Study material / PDF added!");
      setShowMaterialModal(false);
      setMaterialForm({ title: "", description: "", file_url: "", file_name: "", file_size: "" });
      fetchMaterials();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save material");
    } finally {
      setSavingMaterial(false);
    }
  };

  const handleDeleteMaterial = async (material_id) => {
    if (!window.confirm("Delete this study material?")) return;
    try {
      await axios.delete(`${API}/batches/${selectedBatch.batch_id}/materials/${material_id}`, { withCredentials: true });
      toast.success("Material deleted");
      fetchMaterials();
    } catch {
      toast.error("Failed to delete material");
    }
  };

  // Sessions breakdown
  const liveSessions = sessions.filter(s => s.meeting_link || s.session_type === "live");
  // In recorded classes, EVERY session in this batch is listed so live classes appear automatically!
  const recordedSessions = sessions;
  const recordedWithUrlCount = sessions.filter(s => s.recording_url).length;

  const mainContent = (
    <>
      <div className="space-y-6">

        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* VIEW 1: BATCHES LIST (When no batch is selected)                 */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        {!selectedBatch && (
          <div className="space-y-6">
            {/* Top Header Card with rich dark/light mode contrast */}
            <div className={`relative overflow-hidden rounded-3xl p-6 sm:p-8 border shadow-xl transition-all ${
              isLight ? "bg-white border-slate-200" : "bg-[#0E121B] border-white/10"
            }`}>
              <div className="absolute inset-0 bg-gradient-to-br from-violet-600/10 via-fuchsia-600/5 to-transparent pointer-events-none" />
              <div className="relative flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-lg text-white font-black">
                    <Layers className="w-7 h-7" />
                  </div>
                  <div>
                    <h1 className={`text-2xl sm:text-3xl font-black font-outfit tracking-tight ${
                      isLight ? "text-slate-900" : "text-white"
                    }`}>
                      Live Batches
                    </h1>
                    <p className={`text-xs sm:text-sm font-medium mt-1 ${
                      isLight ? "text-slate-600" : "text-zinc-400"
                    }`}>
                      Click on any batch to schedule Zoom live classes. Sessions automatically sync to Recorded Classes.
                    </p>
                  </div>
                </div>

                <Button
                  onClick={openCreateBatch}
                  className="h-11 px-5 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 text-white font-black rounded-2xl shadow-lg hover:opacity-95"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Batch
                </Button>
              </div>
            </div>

            {/* Batches Grid */}
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
              </div>
            ) : batches.length === 0 ? (
              <div className={`text-center py-24 rounded-3xl border border-dashed transition-all ${
                isLight ? "border-slate-300 text-slate-500 bg-white shadow-sm" : "border-white/10 text-zinc-400 bg-[#0E121B]"
              }`}>
                <div className="w-16 h-16 rounded-2xl bg-violet-500/10 text-violet-500 flex items-center justify-center mx-auto mb-3">
                  <Layers className="w-8 h-8 opacity-60" />
                </div>
                <p className="font-bold text-lg text-slate-900 dark:text-white">No batches created yet</p>
                <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1 max-w-sm mx-auto">
                  Create your first live batch to schedule live Zoom sessions and upload study materials.
                </p>
                <Button
                  onClick={openCreateBatch}
                  className="mt-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Create First Batch
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {batches.map(b => (
                  <motion.div
                    key={b.batch_id}
                    layout
                    whileHover={{ y: -4 }}
                    onClick={() => setSelectedBatch(b)}
                    className={`group relative rounded-3xl overflow-hidden border cursor-pointer transition-all shadow-md ${
                      isLight
                        ? "bg-white border-slate-200/90 hover:border-violet-400 hover:shadow-xl"
                        : "bg-[#111622] border-white/[0.08] hover:border-violet-500/50 hover:shadow-2xl"
                    }`}
                  >
                    <div className="h-2 w-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-3 mb-2.5">
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-black text-lg font-outfit truncate group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors ${
                            isLight ? "text-slate-900" : "text-white"
                          }`}>
                            {b.name}
                          </h3>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {b.is_running_batch ? (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-[#00FF88] border border-emerald-500/40 flex items-center gap-1.5 shadow-xs">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                  On Student Dashboard
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => handleToggleDashboard(b, false, e)}
                                  className="text-[10px] font-bold text-rose-500 hover:text-rose-600 underline cursor-pointer"
                                  title="Remove this batch from Student Dashboard"
                                >
                                  Remove from Dashboard
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-500/10 text-slate-500 border border-slate-500/20">
                                  Batches Section Only
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => handleToggleDashboard(b, true, e)}
                                  className="text-[10px] font-bold text-emerald-600 dark:text-[#00FF88] hover:underline cursor-pointer"
                                  title="Show this batch on Student Dashboard"
                                >
                                  + Show on Dashboard
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Card Edit/Delete actions */}
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => openEditBatch(b, e)}
                            className={`p-2 rounded-xl border transition-colors ${
                              isLight
                                ? "border-slate-200 hover:bg-slate-100 text-slate-600"
                                : "border-white/10 hover:bg-white/10 text-zinc-300"
                            }`}
                            title="Edit Batch"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            disabled={deletingBatch === b.batch_id}
                            onClick={(e) => handleDeleteBatch(b, e)}
                            className={`p-2 rounded-xl border text-red-500 transition-colors ${
                              isLight
                                ? "border-red-200 hover:bg-red-50"
                                : "border-red-500/20 hover:bg-red-500/10"
                            }`}
                            title="Delete Batch"
                          >
                            {deletingBatch === b.batch_id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </div>

                      {b.description && (
                        <p className={`text-xs line-clamp-2 mb-4 font-medium ${
                          isLight ? "text-slate-600" : "text-zinc-400"
                        }`}>
                          {b.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-3.5 text-xs mb-5 font-semibold">
                        <span className={`flex items-center gap-1.5 ${isLight ? "text-slate-700" : "text-zinc-300"}`}>
                          <Users className="w-4 h-4 text-violet-500" />
                          {b.enrolled_count || 0} Students
                        </span>
                        {b.start_date && (
                          <span className={`flex items-center gap-1.5 ${isLight ? "text-slate-700" : "text-zinc-300"}`}>
                            <Calendar className="w-4 h-4 text-fuchsia-500" />
                            {fmtDate(b.start_date)}
                          </span>
                        )}
                      </div>

                      <Button
                        size="sm"
                        className="w-full h-10 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-xl shadow-md group-hover:opacity-95"
                      >
                        Enter Batch Room <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* VIEW 2: INSIDE BATCH ROOM                                        */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        {selectedBatch && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Top Navigation & Batch Hero Card */}
            <div className={`p-6 sm:p-7 rounded-3xl border shadow-xl transition-all ${
              isLight ? "bg-white border-slate-200" : "bg-[#0E121B] border-white/10"
            }`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setSelectedBatch(null)}
                    className={`p-2.5 rounded-2xl border transition-all ${
                      isLight
                        ? "border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-800"
                        : "border-white/15 bg-white/5 hover:bg-white/10 text-white"
                    }`}
                    title="Back to All Batches"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>

                  <div>
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <h1 className={`text-2xl sm:text-3xl font-black font-outfit tracking-tight ${
                        isLight ? "text-slate-900" : "text-white"
                      }`}>
                        {selectedBatch.name}
                      </h1>
                      {selectedBatch.is_active ? (
                        <span className="text-[11px] font-black uppercase px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
                          Active Batch
                        </span>
                      ) : (
                        <span className="text-[11px] font-black uppercase px-3 py-1 rounded-full bg-slate-500/15 text-slate-600 dark:text-zinc-400 border border-slate-500/30">
                          Closed
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs font-semibold text-slate-600 dark:text-zinc-300 flex-wrap">
                      <span className="flex items-center gap-1.5">
                        <Users className="w-4 h-4 text-violet-500" />
                        {selectedBatch.enrolled_count || 0} Enrolled Students
                      </span>
                      {selectedBatch.start_date && (
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4 text-fuchsia-500" />
                          {fmtDate(selectedBatch.start_date)}{selectedBatch.end_date && ` → ${fmtDate(selectedBatch.end_date)}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => openEditBatch(selectedBatch, e)}
                    className={`h-10 px-4 rounded-xl text-xs font-bold border ${
                      isLight
                        ? "border-slate-300 text-slate-800 hover:bg-slate-100"
                        : "border-white/15 text-white hover:bg-white/10"
                    }`}
                  >
                    <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                    Edit Batch Info
                  </Button>
                </div>
              </div>

              {selectedBatch.description && (
                <p className={`text-xs sm:text-sm mt-4 pt-4 border-t font-medium ${
                  isLight ? "border-slate-100 text-slate-600" : "border-white/[0.06] text-zinc-300"
                }`}>
                  {selectedBatch.description}
                </p>
              )}
            </div>

            {/* 2 Main Tabs: Live Classes | Recorded Classes */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className={`flex gap-1.5 p-1.5 rounded-2xl border transition-all ${
                isLight ? "bg-slate-100 border-slate-200" : "bg-[#111622] border-white/10"
              }`}>
                {/* Live Classes Tab */}
                <button
                  onClick={() => setActiveTab("live")}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                    activeTab === "live"
                      ? "bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-md"
                      : isLight ? "text-slate-700 hover:text-slate-900" : "text-zinc-300 hover:text-white"
                  }`}
                >
                  <Radio className="w-4 h-4" />
                  <span>Live Classes</span>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                    activeTab === "live" ? "bg-white/20 text-white" : isLight ? "bg-slate-200 text-slate-800" : "bg-white/10 text-zinc-200"
                  }`}>
                    {liveSessions.length}
                  </span>
                </button>

                {/* Recorded Classes Tab */}
                <button
                  onClick={() => setActiveTab("recordings")}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                    activeTab === "recordings"
                      ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md"
                      : isLight ? "text-slate-700 hover:text-slate-900" : "text-zinc-300 hover:text-white"
                  }`}
                >
                  <Video className="w-4 h-4" />
                  <span>Recorded Classes</span>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                    activeTab === "recordings" ? "bg-white/20 text-white" : isLight ? "bg-slate-200 text-slate-800" : "bg-white/10 text-zinc-200"
                  }`}>
                    {recordedSessions.length}
                  </span>
                </button>
              </div>

              {/* Action Buttons for the active tab with radiant colors & high contrast */}
              {activeTab === "live" && (
                <Button
                  onClick={openScheduleLive}
                  className="h-11 px-5 bg-gradient-to-r from-red-600 to-rose-600 text-white font-black rounded-2xl shadow-lg hover:opacity-95"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Schedule Live Class
                </Button>
              )}

              {activeTab === "recordings" && (
                <Button
                  onClick={() => openAddRecording(null)}
                  className="h-11 px-5 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 text-white font-black rounded-2xl shadow-lg hover:opacity-95"
                >
                  <Upload className="w-4 h-4 mr-1.5" />
                  Upload Old / Past Recording
                </Button>
              )}
            </div>

            {/* ───────────────────────────────────────────────────────────── */}
            {/* TAB 1 CONTENT: LIVE CLASSES                                   */}
            {/* ───────────────────────────────────────────────────────────── */}
            {activeTab === "live" && (
              <div className="space-y-4">
                {loadingSessions ? (
                  <div className="py-20 flex justify-center">
                    <Loader2 className="w-10 h-10 animate-spin text-red-500" />
                  </div>
                ) : liveSessions.length === 0 ? (
                  <div className={`p-12 sm:p-16 text-center rounded-3xl border border-dashed transition-all ${
                    isLight ? "bg-white border-slate-300 shadow-sm" : "bg-[#111622] border-white/10"
                  }`}>
                    <div className="w-16 h-16 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-3">
                      <Radio className="w-8 h-8 opacity-70" />
                    </div>
                    <h3 className={`font-black text-lg font-outfit ${isLight ? "text-slate-900" : "text-white"}`}>
                      No Live Classes Scheduled for this Batch
                    </h3>
                    <p className={`text-xs sm:text-sm mt-1 mb-5 max-w-md mx-auto font-medium ${
                      isLight ? "text-slate-600" : "text-zinc-400"
                    }`}>
                      Schedule a Zoom meeting or custom stream for students enrolled in {selectedBatch.name}. Once scheduled, it automatically creates a corresponding slot in Recorded Classes ready for the recording link!
                    </p>
                    <Button onClick={openScheduleLive} className="h-11 px-6 bg-red-600 hover:bg-red-500 text-white font-black rounded-xl shadow-md">
                      <Plus className="w-4 h-4 mr-1.5" />
                      Schedule First Live Class
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3.5">
                    {liveSessions.map(session => {
                      const isLive = session.status === "live";
                      const isCompleted = session.status === "completed";
                      return (
                        <div
                          key={session.session_id}
                          className={`p-5 sm:p-6 rounded-3xl border transition-all ${
                            isLive
                              ? isLight ? "bg-red-50/80 border-red-300 shadow-md" : "bg-red-500/10 border-red-500/40 shadow-lg shadow-red-500/5"
                              : isLight ? "bg-white border-slate-200 shadow-sm" : "bg-[#111622] border-white/[0.08]"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2.5 flex-wrap mb-1.5">
                                {isLive ? (
                                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-red-600 text-white animate-pulse">
                                    <span className="w-2 h-2 rounded-full bg-white" />
                                    Live Now
                                  </span>
                                ) : isCompleted ? (
                                  <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-slate-500/15 text-slate-600 dark:text-zinc-400 border border-slate-500/30">
                                    Completed
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-sky-500/15 text-sky-700 dark:text-sky-400 border border-sky-500/30">
                                    Upcoming
                                  </span>
                                )}

                                <h3 className={`font-black text-lg font-outfit truncate ${
                                  isLight ? "text-slate-900" : "text-white"
                                }`}>
                                  {session.title}
                                </h3>

                                {session.source === "zoom" && (
                                  <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-md bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/30">
                                    Zoom Auto-Meeting
                                  </span>
                                )}
                              </div>

                              {session.description && (
                                <p className={`text-xs sm:text-sm mt-1 line-clamp-2 font-medium ${
                                  isLight ? "text-slate-600" : "text-zinc-400"
                                }`}>
                                  {session.description}
                                </p>
                              )}

                              <div className="flex flex-wrap gap-4 mt-3 text-xs font-semibold text-slate-600 dark:text-zinc-300">
                                <span className="flex items-center gap-1.5">
                                  <Calendar className="w-4 h-4 text-violet-500" />
                                  {fmtDateTime(session.scheduled_at)}
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <Timer className="w-4 h-4 text-fuchsia-500" />
                                  {session.duration || 90} mins
                                </span>
                                {session.zoom_meeting_id && (
                                  <span className="text-slate-500 dark:text-zinc-400">
                                    Zoom ID: {session.zoom_meeting_id}
                                  </span>
                                )}
                              </div>

                              {/* Attached Study Notes PDF with Direct View Action */}
                              {session.pdf_url ? (
                                <div className={`p-2.5 rounded-2xl border flex items-center justify-between gap-2.5 transition-all mt-3 max-w-xl shadow-xs ${
                                  isLight
                                    ? "bg-emerald-50/80 border-emerald-200 hover:bg-emerald-50"
                                    : "bg-emerald-500/10 border-emerald-500/25 hover:bg-emerald-500/15"
                                }`}>
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                                      <FileText className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                                          Study Notes PDF
                                        </span>
                                      </div>
                                      <span className={`text-xs font-bold truncate block mt-0.5 ${
                                        isLight ? "text-slate-900" : "text-white"
                                      }`} title={session.pdf_name || "Study Notes PDF"}>
                                        {session.pdf_name || "Study Notes PDF Attached"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <a
                                      href={session.pdf_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="h-7 px-2.5 rounded-lg text-xs font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1 shadow-sm transition-all"
                                      title="Open PDF"
                                    >
                                      <Eye className="w-3 h-3" />
                                      <span>View PDF</span>
                                    </a>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => openEditLive(session)}
                                  className={`mt-2.5 py-1 px-2.5 rounded-xl border border-dashed text-left text-[11px] font-bold inline-flex items-center gap-1.5 transition-all ${
                                    isLight
                                      ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50/70"
                                      : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                  }`}
                                  title="Attach study notes PDF for this live class"
                                >
                                  <FileText className="w-3.5 h-3.5 text-emerald-500" />
                                  <span>+ Attach Notes PDF</span>
                                </button>
                              )}
                            </div>

                            {/* Live Session Actions */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Host start / join link */}
                              {(session.start_url || session.meeting_link) && (
                                <a
                                  href={session.start_url || session.meeting_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black bg-red-600 hover:bg-red-500 text-white shadow-md transition-all"
                                >
                                  <Radio className="w-4 h-4" />
                                  Start Class (Host)
                                </a>
                              )}

                              <button
                                onClick={() => openEditLive(session)}
                                className={`p-2.5 rounded-xl border transition-colors ${
                                  isLight
                                    ? "border-slate-300 hover:bg-slate-100 text-slate-700"
                                    : "border-white/15 hover:bg-white/10 text-zinc-300"
                                }`}
                                title="Edit Class"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>

                              <button
                                onClick={() => handleDeleteSession(session.session_id)}
                                className={`p-2.5 rounded-xl border text-red-500 transition-colors ${
                                  isLight
                                    ? "border-red-200 hover:bg-red-50"
                                    : "border-red-500/20 hover:bg-red-500/10"
                                }`}
                                title="Delete Class"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ───────────────────────────────────────────────────────────── */}
            {/* TAB 2 CONTENT: RECORDED CLASSES (AUTOMATED FROM LIVE SESSIONS!) */}
            {/* ───────────────────────────────────────────────────────────── */}
            {activeTab === "recordings" && (
              <div className="space-y-5">
                {/* Automation Info Callout with high contrast */}
                <div className={`p-4 sm:p-5 rounded-2xl border flex items-center justify-between gap-4 flex-wrap ${
                  isLight
                    ? "bg-violet-50/80 border-violet-200 text-violet-900"
                    : "bg-violet-500/10 border-violet-500/25 text-violet-200"
                }`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-600 text-white flex items-center justify-center font-black shrink-0">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-black text-sm font-outfit">
                        Automated Recording Sync Active
                      </div>
                      <p className="text-xs opacity-90 font-medium">
                        Live classes scheduled in this batch automatically appear here. Zoom cloud recordings upload automatically with zero manual pasting!
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <Button
                      size="sm"
                      disabled={syncingAllZoom}
                      onClick={handleSyncAllZoomRecordings}
                      className={`h-9 px-3.5 text-xs font-black rounded-xl border flex items-center gap-1.5 shadow-sm transition-transform active:scale-95 ${
                        isLight
                          ? "bg-white border-blue-300 text-blue-700 hover:bg-blue-50"
                          : "bg-[#2D8CFF]/15 border-[#2D8CFF]/40 text-[#2D8CFF] hover:bg-[#2D8CFF]/25"
                      }`}
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${syncingAllZoom ? "animate-spin" : ""}`} />
                      <span>{syncingAllZoom ? "Syncing Zoom..." : "Sync Zoom Cloud"}</span>
                    </Button>
                    <div className="text-xs font-bold px-3 py-1.5 rounded-xl bg-white/70 dark:bg-black/30 border border-violet-300 dark:border-white/10">
                      {recordedWithUrlCount} / {recordedSessions.length} Recordings Uploaded
                    </div>
                  </div>
                </div>

                {loadingSessions ? (
                  <div className="py-20 flex justify-center">
                    <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
                  </div>
                ) : recordedSessions.length === 0 ? (
                  <div className={`p-12 sm:p-16 text-center rounded-3xl border border-dashed transition-all ${
                    isLight ? "bg-white border-slate-300 shadow-sm" : "bg-[#111622] border-white/10"
                  }`}>
                    <div className="w-16 h-16 rounded-2xl bg-violet-500/10 text-violet-500 flex items-center justify-center mx-auto mb-3">
                      <Film className="w-8 h-8 opacity-70" />
                    </div>
                    <h3 className={`font-black text-lg font-outfit ${isLight ? "text-slate-900" : "text-white"}`}>
                      No Classes in this Batch Yet
                    </h3>
                    <p className={`text-xs sm:text-sm mt-1 mb-5 max-w-md mx-auto font-medium ${
                      isLight ? "text-slate-600" : "text-zinc-400"
                    }`}>
                      Schedule a Live Class in the "Live Classes" tab, or click below to directly add a standalone recorded session.
                    </p>
                    <Button onClick={() => openAddRecording(null)} className="h-11 px-6 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 text-white font-black rounded-xl shadow-md">
                      <Upload className="w-4 h-4 mr-1.5" />
                      Upload Old / Past Class Recording
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {recordedSessions.map(session => {
                      const hasRecording = Boolean(session.recording_url);
                      const isZoomSession = Boolean(session.zoom_meeting_id || session.source === "zoom");
                      const isZoomRecording = session.recording_source === "zoom_cloud" || (session.recording_url && session.recording_url.includes("zoom.us"));

                      return (
                        <div
                          key={session.session_id}
                          className={`rounded-3xl border overflow-hidden flex flex-col justify-between transition-all shadow-md ${
                            hasRecording
                              ? isLight
                                ? "bg-white border-slate-200/90 hover:border-violet-400 hover:shadow-xl"
                                : "bg-[#111622] border-white/[0.08] hover:border-violet-500/40 hover:shadow-2xl"
                              : isZoomSession
                              ? isLight
                                ? "bg-blue-50/40 border-blue-300/80 hover:border-blue-400"
                                : "bg-[#2D8CFF]/[0.05] border-[#2D8CFF]/30 hover:border-[#2D8CFF]/50"
                              : isLight
                              ? "bg-amber-50/40 border-amber-300/80 hover:border-amber-400"
                              : "bg-amber-500/[0.04] border-amber-500/25 hover:border-amber-500/40"
                          }`}
                        >
                          <div>
                            {/* Card Top: Video Preview OR Awaiting Link Banner */}
                            {hasRecording ? (
                              <div
                                onClick={() => setPlayingSession(session)}
                                className="relative aspect-video w-full bg-black/60 flex items-center justify-center cursor-pointer group overflow-hidden"
                              >
                                {isZoomRecording ? (
                                  <div className="absolute inset-0 bg-gradient-to-br from-[#0c1a2e]/90 via-[#0e1628]/95 to-black/90 flex flex-col items-center justify-center p-4">
                                    <div className="w-13 h-13 rounded-2xl bg-[#2D8CFF] text-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform mb-2">
                                      <Play className="w-6 h-6 ml-0.5 fill-white" />
                                    </div>
                                    <span className="text-[10px] font-black tracking-wider uppercase px-2.5 py-0.5 rounded-full bg-[#2D8CFF]/20 text-[#2D8CFF] border border-[#2D8CFF]/30">
                                      Zoom Cloud Player
                                    </span>
                                  </div>
                                ) : (
                                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center text-white shadow-2xl group-hover:scale-110 transition-transform">
                                    <Play className="w-6 h-6 ml-0.5 fill-white" />
                                  </div>
                                )}
                                <span className="absolute bottom-2.5 right-2.5 text-[11px] font-black px-2.5 py-0.5 rounded-lg bg-black/85 text-white backdrop-blur-sm z-10">
                                  {session.duration || 90} mins
                                </span>
                              </div>
                            ) : isZoomSession ? (
                              <div className={`p-4 border-b flex items-center justify-between ${
                                isLight ? "bg-blue-100/70 border-blue-200" : "bg-[#2D8CFF]/15 border-[#2D8CFF]/25"
                              }`}>
                                <div className="flex items-center gap-2">
                                  <Video className="w-4 h-4 text-[#2D8CFF] animate-pulse" />
                                  <span className="text-xs font-black text-blue-900 dark:text-blue-300">
                                    Zoom Cloud Auto-Sync
                                  </span>
                                </div>
                                <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400">
                                  Zoom ID: {session.zoom_meeting_id || "Auto"}
                                </span>
                              </div>
                            ) : (
                              <div className={`p-4 border-b flex items-center justify-between ${
                                isLight ? "bg-amber-100/60 border-amber-200" : "bg-amber-500/10 border-amber-500/20"
                              }`}>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-pulse" />
                                  <span className="text-xs font-black text-amber-800 dark:text-amber-300">
                                    Ready for Recording Link
                                  </span>
                                </div>
                                <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400">
                                  {session.duration || 90} min class
                                </span>
                              </div>
                            )}

                            {/* Card Body */}
                            <div className="p-5 space-y-2.5">
                              <div className="flex items-start justify-between gap-2">
                                <div className="space-y-1 truncate pr-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className={`font-black text-base font-outfit truncate ${
                                      isLight ? "text-slate-900" : "text-white"
                                    }`}>
                                      {session.title}
                                    </h4>
                                    {isZoomRecording && (
                                      <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-[#2D8CFF]/15 text-[#2D8CFF] border border-[#2D8CFF]/30">
                                        Zoom Cloud
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <button
                                    onClick={() => openAddRecording(session)}
                                    className={`p-1.5 rounded-lg transition-colors ${
                                      isLight ? "hover:bg-slate-100 text-slate-500" : "hover:bg-white/10 text-zinc-400"
                                    }`}
                                    title="Edit Full Info"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSession(session.session_id)}
                                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-500/10"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>

                              {session.description && (
                                <p className={`text-xs line-clamp-2 font-medium ${
                                  isLight ? "text-slate-600" : "text-zinc-400"
                                }`}>
                                  {session.description}
                                </p>
                              )}

                              <div className="text-[11px] font-semibold text-slate-500 dark:text-zinc-400 flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-violet-500" />
                                <span>{fmtDateTime(session.scheduled_at)}</span>
                              </div>

                              {/* Attached Study Notes PDF with Direct View Action */}
                              {session.pdf_url ? (
                                <div className={`p-2.5 rounded-2xl border flex items-center justify-between gap-2.5 transition-all mt-2.5 shadow-xs ${
                                  isLight
                                    ? "bg-emerald-50/80 border-emerald-200 hover:bg-emerald-50"
                                    : "bg-emerald-500/10 border-emerald-500/25 hover:bg-emerald-500/15"
                                }`}>
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                                      <FileText className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                                          Study Notes PDF
                                        </span>
                                      </div>
                                      <span className={`text-xs font-bold truncate block mt-0.5 ${
                                        isLight ? "text-slate-900" : "text-white"
                                      }`} title={session.pdf_name || "Study Notes PDF"}>
                                        {session.pdf_name || "Study Notes PDF Attached"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <a
                                      href={session.pdf_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="h-7 px-2.5 rounded-lg text-xs font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1 shadow-sm transition-all"
                                      title="Open PDF"
                                    >
                                      <Eye className="w-3 h-3" />
                                      <span>View PDF</span>
                                    </a>
                                    <button
                                      type="button"
                                      onClick={() => openAddRecording(session)}
                                      className={`p-1.5 rounded-lg transition-colors ${
                                        isLight ? "text-slate-400 hover:text-slate-700" : "text-zinc-500 hover:text-zinc-300"
                                      }`}
                                      title="Change / Update Attached PDF"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => openAddRecording(session)}
                                  className={`w-full mt-2 py-1.5 px-3 rounded-xl border border-dashed text-left text-[11px] font-bold flex items-center justify-between transition-all ${
                                    isLight
                                      ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50/70"
                                      : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                  }`}
                                  title="Attach study notes PDF for this class"
                                >
                                  <span className="flex items-center gap-1.5">
                                    <FileText className="w-3.5 h-3.5 text-emerald-500" />
                                    Attach Study Notes PDF
                                  </span>
                                  <Plus className="w-3.5 h-3.5 text-emerald-500" />
                                </button>
                              )}

                              {/* AUTOMATION: Zoom Auto-Detect or Manual Fast Paste */}
                              {!hasRecording && isZoomSession && (
                                <div className={`p-3.5 rounded-2xl border space-y-2.5 mt-3 ${
                                  isLight
                                    ? "bg-blue-50/80 border-blue-200/90 shadow-sm"
                                    : "bg-gradient-to-br from-[#2D8CFF]/10 via-black/40 to-black/20 border-[#2D8CFF]/30"
                                }`}>
                                  <div className="flex items-center gap-2">
                                    <span className="relative flex h-2 w-2">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                                    </span>
                                    <span className={`text-xs font-black ${isLight ? "text-blue-950" : "text-white"}`}>
                                      Auto-Detecting Cloud Recording
                                    </span>
                                  </div>
                                  <p className={`text-[11px] font-medium leading-relaxed ${
                                    isLight ? "text-slate-600" : "text-zinc-400"
                                  }`}>
                                    Recorded on Zoom Cloud. Once Zoom finishes processing, this recording uploads automatically with zero pasting required.
                                  </p>

                                  <div className="flex flex-wrap items-center gap-2 pt-1">
                                    <Button
                                      size="sm"
                                      disabled={syncingZoomSession === session.session_id}
                                      onClick={() => handleSyncZoomSession(session)}
                                      className="h-8 px-3.5 text-xs font-black bg-[#2D8CFF] hover:bg-[#1a77eb] text-white rounded-xl shadow-md flex items-center gap-1.5 transition-transform active:scale-95"
                                    >
                                      {syncingZoomSession === session.session_id ? (
                                        <>
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                          <span>Checking Zoom...</span>
                                        </>
                                      ) : (
                                        <>
                                          <RefreshCw className="w-3 h-3" />
                                          <span>Fetch from Zoom Now</span>
                                        </>
                                      )}
                                    </Button>

                                    <button
                                      type="button"
                                      onClick={() => setManualPasteOpen(p => ({ ...p, [session.session_id]: !p[session.session_id] }))}
                                      className={`text-[10px] font-bold hover:underline transition-colors px-1 py-1 ${
                                        isLight ? "text-slate-500 hover:text-slate-800" : "text-zinc-400 hover:text-white"
                                      }`}
                                    >
                                      {manualPasteOpen[session.session_id] ? "Hide manual input" : "Or paste link manually"}
                                    </button>
                                  </div>

                                  {manualPasteOpen[session.session_id] && (
                                    <div className="pt-2 border-t border-slate-200 dark:border-white/10 space-y-1.5">
                                      <label className="text-[10px] font-bold text-slate-700 dark:text-zinc-300 block">
                                        Manual URL Override (Optional):
                                      </label>
                                      <div className="flex gap-2">
                                        <input
                                          type="text"
                                          placeholder="https://youtube.com/... or Drive"
                                          value={quickLinks[session.session_id] || ""}
                                          onChange={(e) => setQuickLinks({ ...quickLinks, [session.session_id]: e.target.value })}
                                          className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded-xl border outline-none ${
                                            isLight
                                              ? "bg-white border-slate-300 text-slate-900 focus:border-violet-500"
                                              : "bg-black/60 border-white/15 text-white focus:border-violet-500"
                                          }`}
                                        />
                                        <Button
                                          size="sm"
                                          disabled={savingQuickLink === session.session_id}
                                          onClick={() => handleQuickSaveRecording(session)}
                                          className="h-8 px-3 text-xs font-black bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl shadow-sm"
                                        >
                                          {savingQuickLink === session.session_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {!hasRecording && !isZoomSession && (
                                <div className={`p-3 rounded-2xl border space-y-2 mt-3 ${
                                  isLight ? "bg-white border-amber-200 shadow-sm" : "bg-black/30 border-white/10"
                                }`}>
                                  <label className="text-[11px] font-bold text-slate-700 dark:text-zinc-300 block">
                                    Paste Recording Link:
                                  </label>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      placeholder="https://youtube.com/... or Drive"
                                      value={quickLinks[session.session_id] || ""}
                                      onChange={(e) => setQuickLinks({ ...quickLinks, [session.session_id]: e.target.value })}
                                      className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded-xl border outline-none ${
                                        isLight
                                          ? "bg-slate-50 border-slate-300 text-slate-900 focus:bg-white focus:border-violet-500"
                                          : "bg-black/50 border-white/15 text-white focus:border-violet-500"
                                      }`}
                                    />
                                    <Button
                                      size="sm"
                                      disabled={savingQuickLink === session.session_id}
                                      onClick={() => handleQuickSaveRecording(session)}
                                      className="h-8 px-3 text-xs font-black bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl shadow-sm"
                                    >
                                      {savingQuickLink === session.session_id ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        "Save"
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Card Footer if recording is attached */}
                          {hasRecording && (
                            <div className={`p-4 border-t flex items-center justify-between text-xs font-bold ${
                              isLight ? "border-slate-100 bg-slate-50/50" : "border-white/[0.04] bg-white/[0.01]"
                            }`}>
                              <button
                                onClick={() => setPlayingSession(session)}
                                className="text-violet-600 dark:text-violet-400 hover:underline flex items-center gap-1.5"
                              >
                                <Play className="w-3.5 h-3.5" /> Watch Video
                              </button>
                              <button
                                onClick={() => openAddRecording(session)}
                                className="text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
                              >
                                Change Link
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

      </div>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* MODAL: CREATE / EDIT BATCH                                        */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Dialog open={showBatchForm} onOpenChange={setShowBatchForm}>
        <DialogContent className={`max-w-lg w-full p-6 rounded-3xl border shadow-2xl ${
          isLight ? "bg-white border-slate-200" : "bg-[#0F141F] border-white/15"
        }`}>
          <DialogHeader>
            <DialogTitle className={`font-outfit text-xl font-black ${
              isLight ? "text-slate-900" : "text-white"
            }`}>
              {editBatch ? `Edit Batch — ${editBatch.name}` : "Create New Live Batch"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 dark:text-zinc-400">
              Configure batch name, target curriculum, student intake, and schedule.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <FormInput
              label="Batch Name"
              name="name"
              value={batchForm.name}
              onChange={(e) => setBatchForm(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Trading Ki Pathshala Batch 2"
              isLight={isLight}
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormInput
                label="Start Date"
                name="start_date"
                type="date"
                value={batchForm.start_date}
                onChange={(e) => setBatchForm(p => ({ ...p, start_date: e.target.value }))}
                isLight={isLight}
              />
              <FormInput
                label="End Date"
                name="end_date"
                type="date"
                value={batchForm.end_date}
                onChange={(e) => setBatchForm(p => ({ ...p, end_date: e.target.value }))}
                isLight={isLight}
              />
            </div>

            <FormInput
              label="Max Student Seats (optional)"
              name="max_students"
              type="number"
              value={batchForm.max_students}
              onChange={(e) => setBatchForm(p => ({ ...p, max_students: e.target.value }))}
              placeholder="Leave blank for unlimited"
              isLight={isLight}
            />

            <FormTextarea
              label="Batch Description"
              name="description"
              value={batchForm.description}
              onChange={(e) => setBatchForm(p => ({ ...p, description: e.target.value }))}
              placeholder="What will students learn in this batch?"
              isLight={isLight}
            />

            <div className="pt-2">
              <div className={`p-4 rounded-2xl border transition-all ${
                batchForm.is_running_batch
                  ? isLight
                    ? "bg-emerald-50/90 border-emerald-400 shadow-xs"
                    : "bg-emerald-500/10 border-emerald-500/40"
                  : isLight
                  ? "bg-slate-50 border-slate-200"
                  : "bg-white/[0.02] border-white/10"
              }`}>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={batchForm.is_running_batch}
                    onChange={(e) => setBatchForm(p => ({ ...p, is_running_batch: e.target.checked }))}
                    className="w-4 h-4 mt-1 rounded text-emerald-600 focus:ring-emerald-500"
                    data-testid="show-on-dashboard-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-bold ${isLight ? "text-slate-900" : "text-white"}`}>
                        Show this batch on Student Dashboard
                      </span>
                      {batchForm.is_running_batch ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-black rounded-full bg-emerald-500/20 text-emerald-700 dark:text-[#00FF88] border border-emerald-500/40">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          Visible on Dashboard
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-semibold rounded-full bg-slate-500/10 text-slate-500 border border-slate-500/20">
                          Only in Batches System
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mt-1 leading-relaxed ${isLight ? "text-slate-600" : "text-zinc-400"}`}>
                      {batchForm.is_running_batch
                        ? "Checked: This batch appears prominently on the student dashboard with batch details and enrollment access."
                        : "Unchecked: This batch is hidden from the student dashboard and is ONLY visible in the Live Batches system. You can uncheck anytime to remove it."}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex gap-2.5 justify-end pt-3 border-t border-slate-100 dark:border-white/10">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBatchForm(false)}
                className={`h-10 px-4 rounded-xl font-bold border ${
                  isLight ? "border-slate-300 text-slate-700 hover:bg-slate-100" : "border-white/15 text-zinc-300"
                }`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={savingBatch}
                onClick={handleSaveBatch}
                className="h-10 px-5 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 text-white font-black rounded-xl shadow-lg"
              >
                {savingBatch ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                {editBatch ? "Save Changes" : "Create Batch"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* MODAL: SCHEDULE LIVE CLASS (ZOOM / MANUAL LINK)                  */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Dialog open={showLiveModal} onOpenChange={setShowLiveModal}>
        <DialogContent className={`max-w-lg w-full p-6 rounded-3xl border shadow-2xl ${
          isLight ? "bg-white border-slate-200" : "bg-[#0F141F] border-white/15"
        }`}>
          <DialogHeader>
            <DialogTitle className={`font-outfit text-xl font-black flex items-center gap-2.5 ${
              isLight ? "text-slate-900" : "text-white"
            }`}>
              <Radio className="w-5 h-5 text-red-500" />
              {editSession ? "Edit Live Class" : "Schedule Live Class for this Batch"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 dark:text-zinc-400">
              Only students enrolled in {selectedBatch?.name} will receive access. This class will automatically sync to Recorded Classes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <FormInput
              label="Class Title"
              name="title"
              value={liveForm.title}
              onChange={(e) => setLiveForm(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Session 1: Market Structure & Support Resistance"
              isLight={isLight}
              required
            />

            {/* Platform Selector with high contrast */}
            <div className="flex flex-col gap-1.5">
              <label className={`text-xs font-bold uppercase tracking-wider ${isLight ? "text-slate-800" : "text-zinc-200"}`}>
                Meeting Platform:
              </label>
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => setLiveForm(p => ({ ...p, source: "zoom" }))}
                  className={`p-3 rounded-2xl border text-left text-xs transition-all ${
                    liveForm.source === "zoom"
                      ? isLight
                        ? "bg-blue-50 border-blue-500 ring-2 ring-blue-500/20 text-blue-900 font-black shadow-sm"
                        : "bg-blue-500/15 border-blue-500 ring-2 ring-blue-500/30 text-blue-300 font-black"
                      : isLight
                      ? "bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100"
                      : "bg-black/30 border-white/10 text-zinc-400 hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <Radio className="w-4 h-4 text-blue-500" />
                    <span>Zoom Automated</span>
                  </div>
                  <span className="text-[10px] block opacity-80 mt-1 font-medium">
                    {zoomStatus.configured ? "Auto-creates Zoom meeting" : "Uses Server Zoom credentials"}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setLiveForm(p => ({ ...p, source: "manual" }))}
                  className={`p-3 rounded-2xl border text-left text-xs transition-all ${
                    liveForm.source === "manual"
                      ? isLight
                        ? "bg-violet-50 border-violet-500 ring-2 ring-violet-500/20 text-violet-900 font-black shadow-sm"
                        : "bg-violet-500/15 border-violet-500 ring-2 ring-violet-500/30 text-violet-300 font-black"
                      : isLight
                      ? "bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100"
                      : "bg-black/30 border-white/10 text-zinc-400 hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <LinkIcon className="w-4 h-4 text-violet-500" />
                    <span>Paste Custom Link</span>
                  </div>
                  <span className="text-[10px] block opacity-80 mt-1 font-medium">
                    Zoom, Google Meet, YouTube Live
                  </span>
                </button>
              </div>
            </div>

            {liveForm.source === "manual" && (
              <FormInput
                label="Meeting URL (Zoom / Meet / YouTube)"
                name="meeting_link"
                value={liveForm.meeting_link}
                onChange={(e) => setLiveForm(p => ({ ...p, meeting_link: e.target.value }))}
                placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                isLight={isLight}
                required
              />
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <label className={`text-xs font-bold uppercase tracking-wider ${isLight ? "text-slate-800" : "text-zinc-200"}`}>
                  Date
                </label>
                <input
                  type="date"
                  value={liveForm.scheduled_date}
                  onChange={(e) => setLiveForm(p => ({ ...p, scheduled_date: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-xl border text-xs sm:text-sm font-semibold mt-1 outline-none ${
                    isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-black/40 border-white/15 text-white"
                  }`}
                />
              </div>

              <div>
                <label className={`text-xs font-bold uppercase tracking-wider ${isLight ? "text-slate-800" : "text-zinc-200"}`}>
                  Time
                </label>
                <select
                  value={liveForm.scheduled_time}
                  onChange={(e) => setLiveForm(p => ({ ...p, scheduled_time: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-xl border text-xs sm:text-sm font-semibold mt-1 outline-none ${
                    isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-black/40 border-white/15 text-white"
                  }`}
                >
                  {timeOptions.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`text-xs font-bold uppercase tracking-wider ${isLight ? "text-slate-800" : "text-zinc-200"}`}>
                  Duration
                </label>
                <select
                  value={liveForm.duration}
                  onChange={(e) => setLiveForm(p => ({ ...p, duration: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-xl border text-xs sm:text-sm font-semibold mt-1 outline-none ${
                    isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-black/40 border-white/15 text-white"
                  }`}
                >
                  {durationOptions.map(d => (
                    <option key={d} value={d}>{d} mins</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Optional Study Notes PDF Attachment */}
            <div className={`p-3.5 rounded-2xl border space-y-2 ${
              isLight ? "bg-slate-50 border-slate-200" : "bg-black/30 border-white/10"
            }`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-bold flex items-center gap-1.5 ${isLight ? "text-slate-800" : "text-white"}`}>
                  <FileText className="w-3.5 h-3.5 text-emerald-500" />
                  Attach Study Notes PDF (optional)
                </span>
                {liveForm.pdf_url && (
                  <button
                    type="button"
                    onClick={() => setLiveForm(p => ({ ...p, pdf_url: "", pdf_name: "" }))}
                    className="text-[10px] text-rose-500 hover:underline font-bold"
                  >
                    Remove PDF
                  </button>
                )}
              </div>
              {liveForm.pdf_url ? (
                <div className="flex items-center gap-2 p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs">
                  <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span className="font-semibold truncate flex-1">{liveForm.pdf_name || "Study Notes Attached"}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 50 * 1024 * 1024) {
                          toast.error("PDF exceeds 50MB limit");
                          return;
                        }
                        setUploadingLivePdf(true);
                        try {
                          const formData = new FormData();
                          formData.append("file", file);
                          formData.append("kind", "pdf");
                          const res = await axios.post(`${API}/uploads`, formData, {
                            headers: { "Content-Type": "multipart/form-data" },
                            withCredentials: true
                          });
                          const fileUrl = res.data?.file_url || `${API}/uploads/${res.data?.file_id}`;
                          setLiveForm(p => ({ ...p, pdf_url: fileUrl, pdf_name: file.name }));
                          toast.success("PDF attached to live class!");
                        } catch {
                          toast.error("Failed to upload PDF");
                        } finally {
                          setUploadingLivePdf(false);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={uploadingLivePdf}
                      className={`text-xs font-bold rounded-xl h-9 pointer-events-none ${
                        isLight ? "border-slate-300 text-slate-700" : "border-white/15 text-zinc-300"
                      }`}
                    >
                      {uploadingLivePdf ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                      {uploadingLivePdf ? "Uploading..." : "Upload PDF File"}
                    </Button>
                  </label>
                  <span className="text-[11px] text-slate-400 font-medium">or paste link in description</span>
                </div>
              )}
            </div>

            <FormTextarea
              label="Class Agenda / Description (optional)"
              name="description"
              value={liveForm.description}
              onChange={(e) => setLiveForm(p => ({ ...p, description: e.target.value }))}
              placeholder="What topics will be covered in this session?"
              isLight={isLight}
            />

            <div className="flex gap-2.5 justify-end pt-3 border-t border-slate-100 dark:border-white/10">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowLiveModal(false)}
                className={`h-10 px-4 rounded-xl font-bold border ${
                  isLight ? "border-slate-300 text-slate-700 hover:bg-slate-100" : "border-white/15 text-zinc-300"
                }`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={savingSession}
                onClick={handleSaveLiveClass}
                className="h-10 px-5 bg-gradient-to-r from-red-600 to-rose-600 text-white font-black rounded-xl shadow-lg"
              >
                {savingSession ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                {editSession ? "Update Class" : "Schedule Live Class"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* MODAL: UPLOAD OLD / PAST CLASS RECORDING                         */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Dialog open={showRecordingModal} onOpenChange={setShowRecordingModal}>
        <DialogContent className={`max-w-lg w-full p-6 rounded-3xl border shadow-2xl ${
          isLight ? "bg-white border-slate-200" : "bg-[#0F141F] border-white/15"
        }`}>
          <DialogHeader>
            <DialogTitle className={`font-outfit text-xl font-black flex items-center gap-2.5 ${
              isLight ? "text-slate-900" : "text-white"
            }`}>
              <Upload className="w-5 h-5 text-violet-500" />
              {editSession ? "Edit Recorded Class" : "Upload Old / Past Class Recording"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 dark:text-zinc-400">
              Upload past Zoom cloud recordings, YouTube, Google Drive, Vimeo, or direct MP4 archives into this batch.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <FormInput
              label="Session Title"
              name="title"
              value={recordingForm.title}
              onChange={(e) => setRecordingForm(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Session 1: Candlestick Psychology & Orderflow"
              isLight={isLight}
              required
            />

            <FormInput
              label="Video Recording URL"
              name="recording_url"
              value={recordingForm.recording_url}
              onChange={(e) => setRecordingForm(p => ({ ...p, recording_url: e.target.value }))}
              placeholder="https://zoom.us/rec/share/... or YouTube / Google Drive / MP4"
              isLight={isLight}
              required
            />

            {/* Optional Zoom Passcode */}
            <FormInput
              label="Zoom Recording Passcode (optional)"
              name="recording_password"
              value={recordingForm.recording_password}
              onChange={(e) => setRecordingForm(p => ({ ...p, recording_password: e.target.value }))}
              placeholder="e.g. Pass@123 (if your Zoom recording is password-protected)"
              isLight={isLight}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormInput
                label="Date Conducted"
                type="date"
                name="scheduled_date"
                value={recordingForm.scheduled_date}
                onChange={(e) => setRecordingForm(p => ({ ...p, scheduled_date: e.target.value }))}
                isLight={isLight}
              />
              <FormInput
                label="Duration (minutes)"
                type="number"
                name="duration"
                value={recordingForm.duration}
                onChange={(e) => setRecordingForm(p => ({ ...p, duration: e.target.value }))}
                placeholder="90"
                isLight={isLight}
              />
            </div>

            {/* Optional Study Notes PDF Attachment */}
            <div className={`p-3.5 rounded-2xl border ${
              isLight ? "bg-slate-50 border-slate-200" : "bg-white/[0.03] border-white/10"
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-bold flex items-center gap-1.5 ${isLight ? "text-slate-800" : "text-white"}`}>
                  <FileText className="w-3.5 h-3.5 text-emerald-500" />
                  Attach Study Notes PDF (optional)
                </span>
                {recordingForm.pdf_url && (
                  <button
                    type="button"
                    onClick={() => setRecordingForm(p => ({ ...p, pdf_url: "", pdf_name: "" }))}
                    className="text-[10px] text-rose-500 hover:underline font-bold"
                  >
                    Remove PDF
                  </button>
                )}
              </div>
              {recordingForm.pdf_url ? (
                <div className="flex items-center gap-2 p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs">
                  <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span className="font-semibold truncate flex-1">{recordingForm.pdf_name || "Study Notes Attached"}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 50 * 1024 * 1024) {
                          toast.error("PDF exceeds 50MB limit");
                          return;
                        }
                        setUploadingSessionPdf(true);
                        try {
                          const formData = new FormData();
                          formData.append("file", file);
                          formData.append("kind", "pdf");
                          const res = await axios.post(`${API}/uploads`, formData, {
                            headers: { "Content-Type": "multipart/form-data" },
                            withCredentials: true
                          });
                          const fileUrl = res.data?.file_url || `${API}/uploads/${res.data?.file_id}`;
                          setRecordingForm(p => ({ ...p, pdf_url: fileUrl, pdf_name: file.name }));
                          toast.success("PDF attached to recording!");
                        } catch {
                          toast.error("Failed to upload PDF");
                        } finally {
                          setUploadingSessionPdf(false);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={uploadingSessionPdf}
                      className={`text-xs font-bold rounded-xl h-9 pointer-events-none ${
                        isLight ? "border-slate-300 text-slate-700" : "border-white/15 text-zinc-300"
                      }`}
                    >
                      {uploadingSessionPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                      {uploadingSessionPdf ? "Uploading..." : "Upload PDF File"}
                    </Button>
                  </label>
                  <span className="text-[11px] text-slate-400 font-medium">or paste link in description</span>
                </div>
              )}
            </div>

            <FormTextarea
              label="Description / Key Takeaways"
              name="description"
              value={recordingForm.description}
              onChange={(e) => setRecordingForm(p => ({ ...p, description: e.target.value }))}
              placeholder="What was covered in this session?"
              isLight={isLight}
            />

            <div className="flex gap-2.5 justify-end pt-3 border-t border-slate-100 dark:border-white/10">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRecordingModal(false)}
                className={`h-10 px-4 rounded-xl font-bold border ${
                  isLight ? "border-slate-300 text-slate-700 hover:bg-slate-100" : "border-white/15 text-zinc-300"
                }`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={savingRecording || uploadingSessionPdf}
                onClick={handleSaveRecording}
                className="h-10 px-5 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 text-white font-black rounded-xl shadow-lg"
              >
                {savingRecording ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                {editSession ? "Update Recording" : "Upload Recording"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* MODAL: UPLOAD STUDY NOTES & PDFS                                 */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Dialog open={showMaterialModal} onOpenChange={setShowMaterialModal}>
        <DialogContent className={`max-w-lg w-full p-6 rounded-3xl border shadow-2xl ${
          isLight ? "bg-white border-slate-200" : "bg-[#0F141F] border-white/15"
        }`}>
          <DialogHeader>
            <DialogTitle className={`font-outfit text-xl font-black flex items-center gap-2.5 ${
              isLight ? "text-slate-900" : "text-white"
            }`}>
              <FileText className="w-5 h-5 text-emerald-500" />
              Upload Study Notes / PDF
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 dark:text-zinc-400">
              Upload PDF cheat sheets, lecture presentations, or trading checklists for students.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <FormInput
              label="Document Title"
              name="title"
              value={materialForm.title}
              onChange={(e) => setMaterialForm(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Price Action Cheat Sheet & Chart Patterns"
              isLight={isLight}
              required
            />

            {/* File Upload Zone with high contrast */}
            <div className="flex flex-col gap-1.5">
              <label className={`text-xs font-bold uppercase tracking-wider ${isLight ? "text-slate-800" : "text-zinc-200"}`}>
                Upload PDF File:
              </label>
              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf,.doc,.docx,.ppt,.pptx"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className={`p-6 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
                  materialForm.file_url
                    ? isLight
                      ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                      : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : isLight
                    ? "border-slate-300 hover:border-emerald-500 bg-slate-50"
                    : "border-white/15 hover:border-emerald-500/60 bg-black/30"
                }`}
              >
                {uploadingPdf ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                    <span className="text-xs font-bold">Uploading document...</span>
                  </div>
                ) : materialForm.file_url ? (
                  <div className="flex flex-col items-center gap-1.5 text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-500" />
                    <span className="text-xs font-black text-slate-900 dark:text-white truncate max-w-[280px]">
                      {materialForm.file_name || "PDF Document"}
                    </span>
                    <span className="text-[10px] text-slate-500 dark:text-zinc-400 font-bold">{materialForm.file_size} · Click to replace</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-center">
                    <Upload className="w-8 h-8 text-slate-400 dark:text-zinc-500" />
                    <span className="text-xs font-bold text-slate-800 dark:text-zinc-200">
                      Click to choose PDF or document
                    </span>
                    <span className="text-[10px] text-slate-500 dark:text-zinc-400">PDF, DOCX up to 50MB</span>
                  </div>
                )}
              </div>
            </div>

            <FormInput
              label="Or Paste Document Link (Google Drive / CDN URL)"
              name="file_url"
              value={materialForm.file_url}
              onChange={(e) => setMaterialForm(p => ({ ...p, file_url: e.target.value }))}
              placeholder="https://..."
              isLight={isLight}
            />

            <FormTextarea
              label="Document Notes (optional)"
              name="description"
              value={materialForm.description}
              onChange={(e) => setMaterialForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Brief summary or reading instructions for students"
              isLight={isLight}
            />

            <div className="flex gap-2.5 justify-end pt-3 border-t border-slate-100 dark:border-white/10">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowMaterialModal(false)}
                className={`h-10 px-4 rounded-xl font-bold border ${
                  isLight ? "border-slate-300 text-slate-700 hover:bg-slate-100" : "border-white/15 text-zinc-300"
                }`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={savingMaterial || !materialForm.file_url}
                onClick={handleSaveMaterial}
                className="h-10 px-5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-black rounded-xl shadow-lg"
              >
                {savingMaterial ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save Material
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Player Modal */}
      <VideoPlayerModal
        session={playingSession}
        onClose={() => setPlayingSession(null)}
        isLight={isLight}
      />
    </>
  );

  if (embedded) {
    return mainContent;
  }

  return (
    <DashboardLayout title={selectedBatch ? `Batch — ${selectedBatch.name}` : "Live Batch Management"}>
      {mainContent}
    </DashboardLayout>
  );
}

export default function AdminBatchesPage() {
  return <AdminBatchesContent embedded={false} />;
}
